/* factory-animation-adapters.js - Canvas 2D animation adapters for factory entities. */
(function () {
  const Loader = window.FactoryGraphicsLoader || {};

  function object(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function cleanString(value, fallback) {
    const clean = String(value || "").trim();
    return clean || fallback || "";
  }

  function colorForKey(value) {
    let hash = 0;
    const text = cleanString(value, "factory");
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 30%, 55%)`;
  }

  function hashValue(value) {
    let hash = 0;
    const text = cleanString(value, "factory");
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) | 0;
    return Math.abs(hash);
  }

  function isFemaleActor(item) {
    const gender = cleanString(item && item.gender, "").toLowerCase();
    const avatarKey = cleanString(item && item.avatarKey, "").toLowerCase();
    if (gender) return gender.startsWith("f") || gender.includes("woman");
    return avatarKey.includes("woman") || avatarKey.includes("annie") || avatarKey.includes("becky") || avatarKey.includes("cassie") || avatarKey.includes("desire") || avatarKey.includes("ida") || avatarKey.includes("vizzy");
  }

  function colorFromPalette(seed, colors) {
    return colors[hashValue(seed) % colors.length];
  }

  function roundRectPath(ctx, x, y, width, height, radius) {
    if (ctx.roundRect) {
      ctx.roundRect(x, y, width, height, radius);
      return;
    }
    const r = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
  }

  function normalizeAdapterConfig(config) {
    const source = object(config);
    return {
      id: cleanString(source.id, "placeholder"),
      type: cleanString(source.type, "placeholder").toLowerCase(),
      image: cleanString(source.image || source.imageKey, ""),
      frameWidth: Number(source.frameWidth) || 0,
      frameHeight: Number(source.frameHeight) || 0,
      fps: Math.max(1, Number(source.fps) || 8),
      loop: source.loop !== false,
      frames: Array.isArray(source.frames) ? source.frames : [],
      states: object(source.states),
      actions: object(source.actions),
      placeholder: object(source.placeholder),
    };
  }

  class PlaceholderAnimationAdapter {
    constructor(config, runtime) {
      this.config = normalizeAdapterConfig(config);
      this.runtime = runtime || null;
      this.action = "idle";
      this.elapsed = 0;
      this.running = true;
      this.ready = true;
    }

    play(action) {
      const next = cleanString(action, "idle");
      if (next !== this.action) {
        this.action = next;
        this.elapsed = 0;
      }
      this.running = true;
      return this;
    }

    stop() {
      this.running = false;
      return this;
    }

    update(deltaSeconds) {
      if (this.running) this.elapsed += Math.max(0, Number(deltaSeconds) || 0);
      return this;
    }

    isReady() {
      return true;
    }

    getFrame() {
      return { index: Math.floor(this.elapsed * this.config.fps), action: this.action, placeholder: true };
    }

    draw(ctx, entity, options) {
      if (!ctx) return;
      const opts = object(options);
      const item = object(entity);
      const x = Number(item.x ?? opts.x) || 0;
      const y = Number(item.y ?? opts.y) || 0;
      const scale = Number(item.scale ?? opts.scale) || 1;
      const width = Number(item.width || opts.width || this.config.placeholder.width || 34) * scale;
      const height = Number(item.height || opts.height || this.config.placeholder.height || 52) * scale;
      const pulse = this.running ? (Math.sin(this.elapsed * Math.PI * 2) + 1) * 0.5 : 0.25;
      const fill = cleanString(item.color || opts.color || this.config.placeholder.fill, colorForKey(item.id || item.name || this.config.id));
      const seed = cleanString(item.avatarKey || item.name || item.id || this.config.id, "factory-worker");
      const hash = hashValue(seed);
      const female = isFemaleActor(item);
      const skin = colorFromPalette(`${seed}:skin`, ["#d9a477", "#c88f65", "#e1b187", "#b97855", "#8f5f46", "#f0c59b"]);
      const hair = colorFromPalette(`${seed}:hair`, ["#221913", "#3a261b", "#5b3826", "#6f5134", "#d8b15f", "#7c6a57"]);
      const trim = colorFromPalette(`${seed}:trim`, ["#d2a84b", "#8eb0c4", "#c98b66", "#9fbf8c", "#c993bd", "#d7c98f"]);
      const trouser = colorFromPalette(`${seed}:trouser`, ["#27333b", "#352f2b", "#26352e", "#3a3343", "#4a382d"]);
      const bob = this.running ? Math.sin(this.elapsed * 5 + hash) * 0.7 : 0;
      const action = cleanString(item.action || item.state, "idle").toLowerCase();
      const busy = action.includes("run") || action.includes("delegat") || action.includes("queued");
      const headR = width * (female ? 0.17 : 0.16);
      const torsoTop = -height * 0.72 + bob;
      const torsoH = height * 0.38;
      const hipY = -height * 0.22;
      const headY = -height * 0.86 + bob;
      const badgeSide = hash % 2 === 0 ? -1 : 1;
      ctx.save();
      ctx.translate(x, y);
      ctx.globalAlpha = Math.max(0.3, Math.min(Number(opts.opacity ?? 1) || 1, 1));
      ctx.fillStyle = "rgba(0,0,0,0.20)";
      ctx.beginPath();
      ctx.ellipse(0, 3, width * 0.43, Math.max(5, height * 0.10), 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.strokeStyle = trouser;
      ctx.lineWidth = Math.max(2, width * 0.08);
      ctx.beginPath();
      if (female) {
        ctx.moveTo(-width * 0.12, hipY);
        ctx.lineTo(-width * 0.20, -height * 0.04);
        ctx.moveTo(width * 0.12, hipY);
        ctx.lineTo(width * 0.18, -height * 0.04);
      } else {
        const stride = busy ? Math.sin(this.elapsed * 7 + hash) * width * 0.08 : 0;
        ctx.moveTo(-width * 0.10, hipY);
        ctx.quadraticCurveTo(-width * 0.14 - stride, -height * 0.12, -width * 0.18 - stride, -height * 0.02);
        ctx.moveTo(width * 0.10, hipY);
        ctx.quadraticCurveTo(width * 0.14 + stride, -height * 0.12, width * 0.18 + stride, -height * 0.02);
      }
      ctx.stroke();

      if (female) {
        ctx.fillStyle = fill;
        ctx.strokeStyle = "rgba(40,35,30,0.50)";
        ctx.lineWidth = Math.max(1, width * 0.035);
        ctx.beginPath();
        ctx.moveTo(-width * 0.24, torsoTop + torsoH * 0.46);
        ctx.lineTo(width * 0.24, torsoTop + torsoH * 0.46);
        ctx.lineTo(width * 0.32, hipY + height * 0.06);
        ctx.quadraticCurveTo(0, hipY + height * 0.11, -width * 0.32, hipY + height * 0.06);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }

      ctx.fillStyle = fill;
      ctx.strokeStyle = cleanString(opts.stroke || this.config.placeholder.stroke, "rgba(40,35,30,0.52)");
      ctx.lineWidth = Math.max(1, 1.5 * scale);
      ctx.beginPath();
      roundRectPath(ctx, -width * 0.28, torsoTop, width * 0.56, torsoH, 7 * scale);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = female ? "rgba(255,246,226,0.86)" : "rgba(245,238,220,0.88)";
      ctx.beginPath();
      ctx.moveTo(-width * 0.12, torsoTop + height * 0.04);
      ctx.lineTo(0, torsoTop + height * 0.13);
      ctx.lineTo(width * 0.12, torsoTop + height * 0.04);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = trim;
      ctx.beginPath();
      ctx.arc(badgeSide * width * 0.15, torsoTop + height * 0.18, Math.max(1.4, width * 0.035), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(255,255,255,${0.22 + pulse * 0.18})`;
      ctx.beginPath();
      roundRectPath(ctx, -width * 0.16, torsoTop + height * 0.20, width * 0.32, height * 0.07, 4 * scale);
      ctx.fill();

      const armLift = busy ? Math.sin(this.elapsed * 6 + hash) * height * 0.04 : 0;
      ctx.strokeStyle = fill;
      ctx.lineWidth = Math.max(2, width * 0.07);
      ctx.beginPath();
      ctx.moveTo(-width * 0.27, torsoTop + height * 0.11);
      ctx.quadraticCurveTo(-width * 0.39, torsoTop + height * 0.22, -width * 0.28, hipY + armLift);
      ctx.moveTo(width * 0.27, torsoTop + height * 0.11);
      ctx.quadraticCurveTo(width * 0.39, torsoTop + height * 0.22, width * 0.28, hipY - armLift);
      ctx.stroke();
      ctx.fillStyle = skin;
      ctx.beginPath(); ctx.arc(-width * 0.28, hipY + armLift, width * 0.045, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(width * 0.28, hipY - armLift, width * 0.045, 0, Math.PI * 2); ctx.fill();

      if (hash % 5 === 0) {
        ctx.strokeStyle = "#8b6a37";
        ctx.lineWidth = Math.max(1, width * 0.035);
        ctx.beginPath();
        ctx.moveTo(width * 0.30, hipY - armLift);
        ctx.lineTo(width * 0.43, hipY - height * 0.11 - armLift);
        ctx.stroke();
      }

      ctx.fillStyle = skin;
      ctx.beginPath();
      ctx.arc(0, headY, headR, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = hair;
      if (female) {
        ctx.beginPath();
        ctx.arc(-headR * 0.20, headY - headR * 0.20, headR * 1.10, Math.PI * 0.72, Math.PI * 2.12);
        ctx.fill();
        ctx.beginPath(); ctx.ellipse(-headR * 0.95, headY + headR * 0.36, headR * 0.34, headR * 0.72, -0.15, 0, Math.PI * 2); ctx.fill();
        if (hash % 3 === 0) {
          ctx.fillStyle = trim;
          ctx.beginPath(); ctx.arc(headR * 0.72, headY - headR * 0.88, headR * 0.20, 0, Math.PI * 2); ctx.fill();
        }
      } else if (hash % 4 === 0) {
        ctx.fillStyle = "#2e2923";
        ctx.beginPath(); ctx.arc(0, headY - headR * 0.76, headR * 0.95, Math.PI, Math.PI * 2); ctx.fill();
        ctx.fillRect(-headR * 1.22, headY - headR * 0.84, headR * 2.44, headR * 0.34);
      } else if (hash % 4 === 1) {
        ctx.beginPath(); ctx.arc(0, headY - headR * 0.62, headR * 0.96, Math.PI, Math.PI * 2); ctx.fill();
        ctx.fillRect(headR * 0.15, headY - headR * 0.72, headR * 1.10, headR * 0.26);
      } else {
        ctx.beginPath();
        ctx.arc(-headR * 0.08, headY - headR * 0.38, headR * 0.92, Math.PI * 0.95, Math.PI * 2.08);
        ctx.fill();
      }

      ctx.fillStyle = "rgba(38,28,22,0.88)";
      ctx.beginPath(); ctx.arc(headR * 0.38, headY - headR * 0.08, Math.max(0.9, headR * 0.12), 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "rgba(58,38,24,0.74)";
      ctx.lineWidth = Math.max(1, width * 0.025);
      ctx.beginPath();
      ctx.moveTo(headR * 0.18, headY + headR * 0.43);
      ctx.quadraticCurveTo(headR * 0.52, headY + headR * 0.58, headR * 0.82, headY + headR * 0.36);
      ctx.stroke();
      if (!female && hash % 5 < 2) {
        ctx.strokeStyle = hair;
        ctx.lineWidth = Math.max(1.2, width * 0.035);
        ctx.beginPath();
        ctx.moveTo(headR * 0.22, headY + headR * 0.32);
        ctx.lineTo(headR * 0.88, headY + headR * 0.24);
        ctx.stroke();
      }

      ctx.restore();
    }

    render(ctx, entity, options) {
      this.draw(ctx, entity, options);
    }

    dispose() {}
  }

  class SpriteSheetAnimationAdapter {
    constructor(config, runtime) {
      this.config = normalizeAdapterConfig(config);
      this.runtime = runtime || null;
      this.fallback = new PlaceholderAnimationAdapter({ ...this.config, type: "placeholder" }, runtime);
      this.action = "idle";
      this.elapsed = 0;
      this.running = true;
      this.image = null;
      this.failed = false;
      this.loadStarted = false;
    }

    ensureImage() {
      if (this.image || this.failed || this.loadStarted || !this.config.image) return;
      this.loadStarted = true;
      const load = this.runtime && this.runtime.loadImage
        ? this.runtime.loadImage(this.config.image)
        : Loader.loadImage ? Loader.loadImage(this.config.image, this.runtime && this.runtime.manifest) : Promise.resolve(null);
      load.then((image) => {
        this.image = image || null;
        this.failed = !image;
      }).catch(() => {
        this.failed = true;
      });
    }

    play(action) {
      const next = cleanString(action, "idle");
      if (next !== this.action) {
        this.action = next;
        this.elapsed = 0;
      }
      this.running = true;
      this.fallback.play(next);
      this.ensureImage();
      return this;
    }

    stop() {
      this.running = false;
      this.fallback.stop();
      return this;
    }

    update(deltaSeconds) {
      const delta = Math.max(0, Number(deltaSeconds) || 0);
      if (this.running) this.elapsed += delta;
      this.fallback.update(delta);
      this.ensureImage();
      return this;
    }

    isReady() {
      return !!this.image;
    }

    framesForAction() {
      const actionDef = this.config.actions[this.action] || this.config.states[this.action] || this.config.actions.idle || this.config.states.idle || null;
      if (Array.isArray(actionDef)) return actionDef;
      if (actionDef && Array.isArray(actionDef.frames)) return actionDef.frames;
      return this.config.frames;
    }

    getFrame() {
      const frames = this.framesForAction();
      if (!frames.length) return { index: 0, action: this.action, x: 0, y: 0, w: this.config.frameWidth, h: this.config.frameHeight };
      const total = frames.reduce((sum, frame) => sum + Math.max(16, Number(frame.durationMs || frame.duration || 100) || 100), 0);
      let cursor = ((this.elapsed * 1000) % Math.max(16, total));
      for (let i = 0; i < frames.length; i += 1) {
        const duration = Math.max(16, Number(frames[i].durationMs || frames[i].duration || 100) || 100);
        if (cursor <= duration) return { index: i, action: this.action, ...frames[i] };
        cursor -= duration;
      }
      return { index: frames.length - 1, action: this.action, ...frames[frames.length - 1] };
    }

    draw(ctx, entity, options) {
      this.ensureImage();
      if (!ctx || !this.image) {
        this.fallback.draw(ctx, entity, options);
        return;
      }
      const item = object(entity);
      const opts = object(options);
      const frame = this.getFrame();
      const sourceW = Number(frame.w || frame.width || this.config.frameWidth) || this.image.width;
      const sourceH = Number(frame.h || frame.height || this.config.frameHeight) || this.image.height;
      const sourceX = Number(frame.x) || 0;
      const sourceY = Number(frame.y) || 0;
      const scale = Number(item.scale ?? opts.scale) || 1;
      const width = Number(item.width || opts.width || sourceW) * scale;
      const height = Number(item.height || opts.height || sourceH) * scale;
      const anchorX = Number(frame.anchorX ?? opts.anchorX ?? 0.5);
      const anchorY = Number(frame.anchorY ?? opts.anchorY ?? 1);
      const x = Number(item.x ?? opts.x) || 0;
      const y = Number(item.y ?? opts.y) || 0;
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(Number(opts.opacity ?? 1) || 1, 1));
      ctx.imageSmoothingEnabled = opts.imageSmoothing !== false;
      try {
        ctx.drawImage(this.image, sourceX, sourceY, sourceW, sourceH, x - width * anchorX, y - height * anchorY, width, height);
      } catch (_) {
        this.fallback.draw(ctx, entity, options);
      }
      ctx.restore();
    }

    render(ctx, entity, options) {
      this.draw(ctx, entity, options);
    }

    dispose() {
      this.image = null;
    }
  }

  function createAdapter(config, runtime) {
    const normalized = normalizeAdapterConfig(config);
    if (normalized.type === "spritesheet" || normalized.type === "sprite-sheet") return new SpriteSheetAnimationAdapter(normalized, runtime);
    return new PlaceholderAnimationAdapter(normalized, runtime);
  }

  const api = {
    normalizeAdapterConfig,
    createAdapter,
    PlaceholderAnimationAdapter,
    SpriteSheetAnimationAdapter,
  };

  window.PlaceholderAnimationAdapter = PlaceholderAnimationAdapter;
  window.SpriteSheetAnimationAdapter = SpriteSheetAnimationAdapter;
  window.FactoryAnimationAdapters = api;
  window.FactoryGraphics = { ...(window.FactoryGraphics || {}), animations: api };
})();
